// --- Dependencies -------------------------------------------------------------------------------
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useOrganization from "@/hooks/useOrganization";
import usePermission from "@/hooks/usePermission";
import useTranslation from "@/hooks/useTranslation";
import type { TyHome } from "@/types";
import { catalogUrl, formatBytes, formatDate, formatMoney, formatMonth, readLangValue } from "@/utils";
import Chart from "@/UI/atoms/Chart";
import Icon from "@/UI/atoms/Icon";
import type { TyIconName } from "@/UI/atoms/Icon/types";
import StatusChip from "@/UI/atoms/StatusChip";
import FollowUpCard from "@/UI/cards/FollowUpCard";
import StatCard from "@/UI/cards/StatCard";
import WorkCard from "@/UI/cards/WorkCard";
import ChartCard from "@/UI/components/ChartCard";
import HeaderPanel from "@/UI/components/HeaderPanel";
import WorkPanel from "@/UI/components/WorkPanel";
import "./styles.css";
// --- Types --------------------------------------------------------------------------------------
type TyWorkRow = { key: keyof TyHome["work"]; icon: TyIconName; label: string; to?: string; tone: "late" | "soon" };
// --- Logic --------------------------------------------------------------------------------------
/**
 * The first screen behind the gate, and **a worklist before it is a report** —
 * `ai.plans/clients-home.md` is why it is shaped this way.
 *
 * Three bands, ordered by urgency rather than by hierarchy. A `member` opens this to find what they
 * owe today; a `manager` for that plus the shape of the pipeline; an `owner` because a period ends on
 * a date and a bucket fills up. So the work comes first, the company's state second, and the owner's
 * band last — it is the band two of the three roles cannot see at all.
 *
 * It is also still the proof the whole chain works: token, organization header, role and subscription
 * state all have to be right for any of these numbers to arrive.
 */
export default function HomeScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const navigate = useNavigate();
    const { canAccess } = usePermission();
    const client = useAuth((state) => state.client);
    const memberships = useAuth((state) => state.memberships);
    const organizationId = useOrganization((state) => state.organizationId);

    const { data, loading, error } = useFetch<TyHome>("/home");

    const organization = memberships.find((item) => item.organization._id === organizationId)?.organization ?? null;

    const hour = new Date().getHours();
    const greeting = hour < 12 ? t.home.greetingMorning : hour < 17 ? t.home.greetingAfternoon : t.home.greetingEvening;

    /**
     * Six counts, each with the drawing that says what kind of work it is, and two of them with
     * nowhere to go: this app has no reservations screen and no contracts screen, so those are
     * counted without pretending to link.
     */
    const workRows: TyWorkRow[] = [
        { key: "followUpsOverdue", icon: "phone", label: t.home.work.followUpsOverdue, to: "/crm/leads", tone: "late" },
        { key: "followUpsToday", icon: "calendar", label: t.home.work.followUpsToday, to: "/crm/leads", tone: "soon" },
        { key: "installmentsOverdue", icon: "subscription", label: t.home.work.installmentsOverdue, to: "/crm/installments", tone: "late" },
        { key: "reservationsExpiring", icon: "history", label: t.home.work.reservationsExpiring, tone: "soon" },
        { key: "contractsUnsigned", icon: "document", label: t.home.work.contractsUnsigned, tone: "soon" },
        { key: "leadsStale", icon: "crm", label: t.home.work.leadsStale, to: "/crm/leads", tone: "late" },
    ];

    const liveWork = workRows.filter((row) => (data?.work[row.key] ?? 0) > 0);

    /** A populated reference, or the bare id it was left as. Both arrive from the API. */
    const idOf = (value: { _id: string } | string | null) => (typeof value === "string" ? value : (value?._id ?? null));

    /**
     * Where a follow-up tile goes: its lead when it has one, and otherwise the person — chasing an
     * installment or arranging a handover is a real contact with no enquiry behind it.
     */
    const openFollowUp = (interaction: TyHome["followUps"][number]) => {
        const leadId = idOf(interaction.lead);
        if (leadId) return navigate(`/crm/leads/${leadId}`);

        const customerId = idOf(interaction.customer);
        if (customerId) navigate(`/crm/customers/${customerId}`);
    };

    const storage = data?.restricted?.storage;
    const subscription = data?.restricted?.subscription;
    const team = data?.restricted?.team;
    const teamTotal = team ? team.owner + team.manager + team.member : 0;
    const storageLeft = storage ? Math.max(0, storage.allowed - storage.used) : 0;
    const storagePercent = storage?.allowed ? Math.min(100, Math.round((storage.used / storage.allowed) * 100)) : 0;

    return (
        <section className="home-screen">
            {/* --- Who you are, which company, and the two places you most often go next ------- */}
            <HeaderPanel
                head={
                    <header className="home-screen__hello">
                        <div className="home-screen__hello-text">
                            {/* The greeting is the smallest line of the three: it is the courtesy,
                                not the information. The name and the company are what you check. */}
                            <span className="home-screen__greeting">{greeting}</span>
                            <h1 className="home-screen__name">{client?.name}</h1>
                            {organization && <p className="home-screen__company">{readLangValue(organization.name, lang)}</p>}
                        </div>

                        <div className="home-screen__links">
                            {organization && (
                                <a className="home-screen__link" href={catalogUrl(organization.slug)} target="_blank" rel="noreferrer">
                                    <Icon name="eye" size={16} />
                                    <span>{t.home.catalogLabel}</span>
                                </a>
                            )}

                            <button type="button" className="home-screen__link" onClick={() => navigate("/profile")}>
                                <Icon name="team" size={16} />
                                <span>{t.home.profileLabel}</span>
                            </button>
                        </div>
                    </header>
                }
            />

            {loading && <p className="home-screen__state">{t.common.loading}</p>}
            {error && <p className="home-screen__state home-screen__state--error">{error}</p>}

            {data && (
                <>
                    {data.access === "free" && <p className="home-screen__banner">{t.home.freeQuota}</p>}
                    {data.access === "readOnly" && <p className="home-screen__banner home-screen__banner--warning">{t.home.readOnly}</p>}

                    {/* --- Band 1: the counts and the calls owed, in one row ------------------ */}
                    <WorkPanel isEmpty={!liveWork.length && !data.followUps.length}>
                        {liveWork.map((row) => (
                            <WorkCard
                                key={row.key}
                                icon={row.icon}
                                count={data.work[row.key]}
                                label={row.label}
                                tone={row.tone}
                                onOpen={row.to ? () => navigate(row.to!) : undefined}
                            />
                        ))}

                        {data.followUps.map((interaction) => (
                            <FollowUpCard key={interaction._id} interaction={interaction} onOpen={() => openFollowUp(interaction)} />
                        ))}
                    </WorkPanel>

                    {/* --- Band 2: where the company stands. Tiles for the exact numbers... --- */}
                    <div className="stat-row">
                        <StatCard icon="properties" value={data.totals.projects} label={t.home.projects} onOpen={() => navigate("/properties")} />
                        <StatCard icon="building" value={data.totals.units} label={t.home.units} onOpen={() => navigate("/properties")} />
                        <StatCard icon="crm" value={data.totals.customers} label={t.home.customers} onOpen={() => navigate("/crm/customers")} />
                        <StatCard icon="stat" value={data.totals.leads} label={t.home.leads} onOpen={() => navigate("/crm/leads")} />
                    </div>

                    {/* ...and charts for the shape, which is what a tile cannot say ----------- */}
                    <div className="home-screen__charts">
                        <ChartCard title={t.home.shelfTitle} figure={String(data.totals.units)} onOpen={() => navigate("/properties")}>
                            <Chart
                                kind="doughnut"
                                size="sm"
                                caption={t.home.shelfTitle}
                                emptyLabel={t.home.nothingYet}
                                labels={constants.unitStatuses.map((status) => t.properties.statuses[status])}
                                /* The colours mean something here: on the shelf, out on hold, gone. */
                                itemColors={["green", "gold", "navy", "slate"]}
                                series={[
                                    { label: t.home.units, color: "gold", data: constants.unitStatuses.map((status) => data.units[status] ?? 0) },
                                ]}
                            />
                        </ChartCard>

                        <ChartCard title={t.home.pipelineTitle} figure={String(data.totals.leads)} onOpen={() => navigate("/crm/leads")}>
                            {/* Horizontal, because Arabic stage names are long and a vertical axis clips them. */}
                            <Chart
                                kind="hbar"
                                size="md"
                                caption={t.home.pipelineTitle}
                                emptyLabel={t.home.nothingYet}
                                labels={constants.leadStages.map((stage) => t.crm.stages[stage])}
                                /* Deepening toward the close, green for won, and lost greyed out of the way. */
                                itemColors={["slate", "goldLight", "gold", "navy", "green", "faint"]}
                                series={[{ label: t.home.leads, color: "gold", data: constants.leadStages.map((stage) => data.leads[stage] ?? 0) }]}
                            />
                        </ChartCard>

                        {/* The money comes before the trend: one is owed on a date, the other is history. */}
                        <ChartCard
                            title={t.home.collectionsTitle}
                            figure={`${formatMoney(data.collections.collected + data.collections.upcoming + data.collections.overdue)} ${t.properties.currency}`}
                            onOpen={() => navigate("/crm/installments")}
                        >
                            <Chart
                                kind="doughnut"
                                size="sm"
                                caption={t.home.collectionsTitle}
                                emptyLabel={t.home.nothingYet}
                                formatValue={(value) => `${formatMoney(value)} ${t.properties.currency}`}
                                labels={[t.home.collected, t.home.upcoming, t.home.overdue]}
                                itemColors={["green", "slate", "red"]}
                                series={[
                                    {
                                        label: t.home.collectionsTitle,
                                        color: "green",
                                        data: [data.collections.collected, data.collections.upcoming, data.collections.overdue],
                                    },
                                ]}
                            />
                        </ChartCard>

                        {/* Six buckets against two series needs the width — it is a line on its own
                            at every size, never half a row. */}
                        <div className="home-screen__charts-wide">
                            <ChartCard title={t.home.trendTitle}>
                                <Chart
                                    kind="area"
                                    size="md"
                                    caption={t.home.trendTitle}
                                    emptyLabel={t.home.nothingYet}
                                    labels={data.trend.months.map((month) => formatMonth(month, lang))}
                                    series={[
                                        { label: t.home.trendLeads, color: "gold", data: data.trend.leads },
                                        { label: t.home.trendDeals, color: "navy", data: data.trend.deals },
                                    ]}
                                />
                            </ChartCard>
                        </div>
                    </div>

                    {/* --- Band 3: owner and manager only. The API already left it out for a member. */}
                    {canAccess("subscription") && data.restricted && (
                        <div className="home-screen__charts">
                            {storage && (
                                <ChartCard
                                    title={t.home.storageTitle}
                                    figure={`${storagePercent}%`}
                                    onOpen={() => navigate("/drive")}
                                    note={t.home.storageOf
                                        .replace("{used}", formatBytes(storage.used, lang))
                                        .replace("{allowed}", formatBytes(storage.allowed, lang))}
                                >
                                    <Chart
                                        kind="doughnut"
                                        size="sm"
                                        caption={t.home.storageTitle}
                                        emptyLabel={t.home.nothingYet}
                                        formatValue={(value) => formatBytes(value, lang)}
                                        labels={[t.home.storageUsed, t.home.storageFree]}
                                        itemColors={["gold", "faint"]}
                                        series={[{ label: t.home.storageTitle, color: "gold", data: [storage.used, storageLeft] }]}
                                    />
                                </ChartCard>
                            )}

                            {team && (
                                <ChartCard title={t.home.teamTitle} figure={String(teamTotal)} onOpen={() => navigate("/team")}>
                                    <Chart
                                        kind="doughnut"
                                        size="sm"
                                        caption={t.home.teamTitle}
                                        emptyLabel={t.home.nothingYet}
                                        labels={[t.roles.owner, t.roles.manager, t.roles.member]}
                                        itemColors={["gold", "navy", "slate"]}
                                        series={[{ label: t.home.teamTitle, color: "gold", data: [team.owner, team.manager, team.member] }]}
                                    />
                                </ChartCard>
                            )}

                            {/* Not a chart. Days left is one number with a deadline on it, and a chart
                                of one number is decoration. */}
                            <article className="home-screen__sub">
                                <header className="home-screen__sub-head">
                                    <h2 className="home-screen__sub-title">{t.home.subscriptionTitle}</h2>
                                    {subscription && (
                                        <StatusChip
                                            label={t.subscription.statuses[subscription.status]}
                                            tone={subscription.status === "grace" ? "danger" : "available"}
                                            size="sm"
                                        />
                                    )}
                                </header>

                                {subscription ? (
                                    <>
                                        <strong className="home-screen__sub-days">
                                            {subscription.daysLeft === null
                                                ? t.subscription.periods[subscription.period]
                                                : subscription.daysLeft === 0
                                                  ? t.home.subscriptionLastDay
                                                  : t.home.subscriptionDaysLeft.replace("{days}", String(subscription.daysLeft))}
                                        </strong>

                                        {subscription.endsAt && (
                                            <p className="home-screen__sub-note">
                                                {t.home.subscriptionEndsAt.replace("{date}", formatDate(subscription.endsAt, lang))}
                                            </p>
                                        )}

                                        {subscription.graceEndsAt && (
                                            <p className="home-screen__sub-note home-screen__sub-note--late">
                                                {t.home.subscriptionGrace.replace("{date}", formatDate(subscription.graceEndsAt, lang))}
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <strong className="home-screen__sub-days">{t.home.subscriptionNone}</strong>
                                )}

                                {data.restricted.hasPendingRequest && <p className="home-screen__sub-note">{t.home.subscriptionPending}</p>}
                                {data.restricted.hasQueuedRenewal && <p className="home-screen__sub-note">{t.home.subscriptionQueued}</p>}

                                <button type="button" className="home-screen__sub-link" onClick={() => navigate("/subscription")}>
                                    <span>{t.home.manage}</span>
                                    <Icon name="chevron" size={14} />
                                </button>
                            </article>
                        </div>
                    )}
                </>
            )}
        </section>
    );
}
